/**
 * Orders placed in this browser tab, kept as `{ [orderId]: previewLink }` in sessionStorage.
 *
 * Having an entry means the order was placed here, which is what lets a reload stay on the
 * confirmation step instead of redirecting to the order detail page. The value is the preview link
 * generated for the confirmation, or an empty string while there is none, so a reload reuses the
 * link instead of minting a new one.
 */
const STORAGE_KEY = 'oc.placedOrders';

function readPlacedOrders(): Record<string, string> {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
    return stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
  } catch {
    return {};
  }
}

function writePlacedOrders(placedOrders: Record<string, string>) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(placedOrders));
  } catch (error) {
    // storage can be full or blocked, the confirmation then just doesn't survive a reload
    console.error('Failed to persist placed orders', error);
  }
}

export function isOrderPlacedInSession(orderId: string | number): boolean {
  return String(orderId) in readPlacedOrders();
}

// keeps an already stored preview link, so restoring the confirmation doesn't wipe it
export function rememberPlacedOrder(orderId: string | number) {
  const placedOrders = readPlacedOrders();
  if (String(orderId) in placedOrders) return;
  writePlacedOrders({ ...placedOrders, [orderId]: '' });
}

export function getPlacedOrderPreviewLink(orderId: string | number): string | undefined {
  return readPlacedOrders()[String(orderId)] || undefined;
}

export function setPlacedOrderPreviewLink(orderId: string | number, previewLink: string) {
  writePlacedOrders({ ...readPlacedOrders(), [orderId]: previewLink });
}
